import { cn } from '@/lib/utils'

import { CompactPrice } from './CompactPrice'
import * as variants from './compactProductCard.config'
import { NewBadge } from './NewBadge'
import { RewardStarIcon } from './RewardStarIcon'
import { LABELS } from '../../lib/labels'
import { truncateCombinedNames, truncateName } from '../../lib/text'

import type { ProductCardProps } from '../../types'

/**
 * CompactProductCard - Minimalist product card for the homepage.
 *
 * Distinct from the tall PDP-density ProductCard. Layout (chosen design):
 * - Height follows content (no forced square) so it stays compact.
 * - The whole card is a single link to the product (no inner anchors, no button).
 * - Row 1: 2 columns — product image (top-left, fixed frame) | product name.
 * - Bottom block (full width): reward points (when present) + price.
 *
 * No description — intentionally sparse. Reuses PriceDisplay + the New
 * badge + the name-truncation helpers from the ProductCard family.
 *
 * The cart button comes from the caller through `renderActions`; without it
 * the card renders no cart button. Below md it is a full-width row under the
 * price, from md up a 25% column beside it.
 *
 * @example
 * <CompactProductCard product={product} renderActions={(p) => <AddToCartButton sku={p.sku} iconFromMd />} />
 */
export function CompactProductCard({
  product,
  className,
  priceSuffix = LABELS.PRICE_SUFFIX,
  imageLoading = 'lazy',
  renderActions,
}: ProductCardProps) {
  const canAddToCart =
    product.productType === 'simple' &&
    product.isInStock !== false &&
    product.showAddToCart !== false

  return (
    <div className={cn(variants.compactProductCardContainerVariants(), className)}>
      {product.isNew && <NewBadge />}

      {/* Clickable area — link wraps image + name only */}
      <a
        href={product.url}
        title={product.name}
        className={variants.compactProductCardClickableVariants()}
      >
        {/* Row 1: image (left) | name (right) */}
        <div className={variants.compactProductCardRowVariants()}>
          <CompactImage name={product.name} imageUrl={product.imageUrl} imageLoading={imageLoading} />
          <CompactName name={product.name} h2ProductName={product.h2ProductName} />
        </div>
      </a>

      {/* Bottom block — outside the link (HTML validity) */}
      <div className={variants.compactProductCardBottomVariants()}>
        {/* Rând 1: reward points full width */}
        <CompactRewardPoints points={product.rewardPoints} />
        {/* Rând 2: sub md preț, apoi buton pe toată lățimea; de la md preț (75%) + buton (25%) */}
        <div className={variants.compactProductCardPriceBlockVariants()}>
          <a
            href={product.url}
            className={variants.compactProductCardPriceLinkVariants()}
          >
            <CompactPrice
              price={product.price}
              specialPrice={product.specialPrice}
              msrp={product.msrp}
              priceSuffix={priceSuffix}
            />
          </a>
          {renderActions && (canAddToCart ? (
            <div className="flex justify-center md:w-1/4">
              {renderActions(product)}
            </div>
          ) : (
            // Sub md ține locul butonului (30px), ca prețurile să rămână aliniate în slider
            <div className="h-[30px] md:hidden" aria-hidden="true" />
          ))}
        </div>
      </div>
    </div>
  )
}

const PLACEHOLDER_IMAGE = '/media/catalog/product/placeholder/default/placeholder.jpg'

interface CompactImageProps {
  name: string
  imageUrl?: string
  imageLoading?: 'lazy' | 'eager'
}

/**
 * Product image — fixed-height frame, top-left.
 *
 * Bounded to the frame (max-w/max-h) and rendered at its natural aspect ratio
 * (w-auto/h-auto + object-contain): never stretched, never cropped (no
 * object-cover). The tiny font keeps the alt fallback small if the image fails.
 */
function CompactImage({ name, imageUrl, imageLoading = 'lazy' }: CompactImageProps) {
  return (
    <div className={variants.compactProductCardImageFrameVariants()}>
      <img
        src={imageUrl || PLACEHOLDER_IMAGE}
        alt={name}
        loading={imageLoading}
        className={variants.compactProductCardImageVariants()}
      />
    </div>
  )
}

interface CompactNameProps {
  name: string
  h2ProductName?: string | null
}

/** Product name — top-aligned, left, clamped to stay compact. */
function CompactName({ name, h2ProductName }: CompactNameProps) {
  const [adjustedName, adjustedH2Name] = truncateCombinedNames(truncateName(name), h2ProductName)

  return (
    <div className={variants.compactProductCardNameContainerVariants()}>
      <p className={variants.compactProductCardNameVariants()}>
        {adjustedName}
        {adjustedH2Name && (
          <span className={variants.compactProductCardNameSecondaryVariants()}>
            {adjustedH2Name}
          </span>
        )}
      </p>
    </div>
  )
}

interface CompactRewardPointsProps {
  points?: number | null
}

/** Compact reward-points line shown just above the price. */
function CompactRewardPoints({ points }: CompactRewardPointsProps) {
  if (!points || points <= 0) return null

  return (
    <div className={variants.compactProductCardRewardRowVariants()}>
      <RewardStarIcon className="w-3.5 h-3.5 shrink-0" />
      <span className="react-css font-main-font text-[11px] xl:text-xs font-medium leading-none">
        {points} {LABELS.REWARD_POINTS_SUFFIX}
      </span>
    </div>
  )
}
